import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUser, 
  FaRupeeSign, 
  FaUsers, 
  FaShieldAlt, 
  FaHeadset, 
  FaWallet, 
  FaGift, 
  FaShoppingBag, 
  FaBox, 
  FaComments,
  FaHistory,
  FaGraduationCap,
  FaBook,
  FaGavel,
  FaCog,
  FaStore,
  FaChevronRight,
  FaSignOutAlt
} from 'react-icons/fa';

const More = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const menuItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: FaUser,
      path: '/profile',
      show: true
    },
    {
      id: 'ways-to-earn',
      label: 'Ways to earn',
      icon: FaRupeeSign,
      path: '/profile?tab=wallet',
      show: user?.role === 'rider'
    },
    {
      id: 'refer-earn',
      label: 'Refer & Earn',
      icon: FaUsers,
      path: '/refer-earn',
      show: true,
      badge: '10,000+ riders are earning referral bonus weekly',
      badgeColor: 'text-green-600'
    },
    {
      id: 'partner-club',
      label: 'Partner club',
      icon: FaShieldAlt,
      path: '/partner-club',
      show: user?.role === 'rider',
      iconColor: 'text-blue-600'
    },
    {
      id: 'wrong-action',
      label: 'Wrong Action',
      icon: FaComments,
      path: '/wrong-action',
      show: true
    },
    {
      id: 'help-support',
      label: 'Help & Support',
      icon: FaHeadset,
      path: '/help',
      show: true
    },
    {
      id: 'floating-cash',
      label: 'Floating Cash',
      icon: FaWallet,
      path: '/floating-cash',
      show: user?.role === 'rider'
    },
    {
      id: 'special-benefits',
      label: 'Special Benefits',
      icon: FaShoppingBag,
      path: '/special-benefits',
      show: true,
      badge: 'New',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 'store',
      label: 'SastaMastaRide Store',
      icon: FaStore,
      path: '/store',
      show: true
    },
    {
      id: 'onboarding-kit',
      label: 'Track onboarding kit',
      icon: FaGift,
      path: '/onboarding-kit',
      show: user?.role === 'rider',
      badge: 'New',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 'message-center',
      label: 'Message center',
      icon: FaComments,
      path: '/messages',
      show: true
    }
  ];

  const textOnlyItems = [
    {
      id: 'login-history',
      label: 'Login History',
      path: '/login-history',
      show: true
    },
    {
      id: 'school',
      label: 'SastaMastaRide School',
      path: '/school',
      show: true
    },
    {
      id: 'skills',
      label: 'SastaMastaRide Skills',
      path: '/skills',
      show: true
    },
    {
      id: 'legal',
      label: 'Legal',
      path: '/legal',
      show: true
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/profile?tab=edit',
      show: true
    }
  ];

  const handleItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 md:hidden">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">More</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Menu Items with Icons */}
        <div className="space-y-1 mb-6">
          {menuItems.filter(item => item.show).map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="flex items-center justify-between p-4 bg-white rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer border-b border-gray-100"
              >
                <div className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                    item.iconColor ? item.iconColor : 'bg-gray-100'
                  }`}>
                    <Icon className={`text-lg ${item.iconColor || 'text-gray-700'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">{item.label}</p>
                    {item.badge && typeof item.badge === 'string' && item.badge.includes('+') && (
                      <p className={`text-xs mt-1 ${item.badgeColor || 'text-gray-500'}`}>
                        {item.badge}
                      </p>
                    )}
                  </div>
                </div>
                {item.badge && typeof item.badge === 'string' && !item.badge.includes('+') && (
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.badgeColor || 'bg-blue-600 text-white'}`}>
                    {item.badge}
                  </span>
                )}
                <FaChevronRight className="text-gray-400 ml-2" />
              </div>
            );
          })}
        </div>

        {/* Text-Only Items */}
        <div className="space-y-1 mb-6">
          {textOnlyItems.filter(item => item.show).map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="flex items-center justify-between p-4 bg-white rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer border-b border-gray-100"
            >
              <p className="text-gray-900 font-medium">{item.label}</p>
              <FaChevronRight className="text-gray-400" />
            </div>
          ))}
        </div>

        {/* Logout Option */}
        <div className="mt-6 mb-4">
          <div
            onClick={handleLogout}
            className="flex items-center justify-between p-4 bg-white rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors cursor-pointer border border-blue-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mr-4 bg-blue-100">
                <FaSignOutAlt className="text-lg text-blue-600" />
              </div>
              <p className="text-blue-600 font-medium">Logout</p>
            </div>
            <FaChevronRight className="text-gray-400" />
          </div>
        </div>

        {/* App Version */}
        <div className="text-center text-xs text-gray-500 mt-8 mb-4">
          App version 1.0.0 | Bundle 1.0.0
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Confirm Logout</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to logout? You will need to login again to access your account.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelLogout}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex-1"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default More;

